import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import {
  createLoanSubledger,
  deleteLoanSubledger,
  getLoanSubledger,
  getWarehouseLineOptions,
  updateLoanSubledger,
} from 'services'
import { svgLoading } from 'stories/assets'
import { confirm, formatDate, thousandSeperator } from 'utils'

import type { ILoanSubledgerRow } from '../WarehouseLines/types'
import { EditLoanSubledgerDialog } from './EditLoanSubledgerDialog'

const formatAmount = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return '-'
  return `$${thousandSeperator(value)}`
}

const formatPercent = (value?: number) => {
  if (!value && value !== 0) return '-'
  return `${(Number(value) * 100).toFixed(3)}%`
}

export const LoanSubledger = () => {
  const [isLoading, setLoading] = useState(false)
  const [rows, setRows] = useState<ILoanSubledgerRow[]>([])
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [warehouseLineOptions, setWarehouseLineOptions] = useState<Record<string, string>>({})
  const [editItem, setEditItem] = useState<Partial<ILoanSubledgerRow> | null>(null)

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const [result, options] = await Promise.all([getLoanSubledger(), getWarehouseLineOptions()])
      setRows(result.results || [])
      setTotals(result.totals || {})
      setWarehouseLineOptions(
        (options || []).reduce((acc: Record<string, string>, line: { name: string }) => {
          acc[line.name] = line.name
          return acc
        }, {}),
      )
    } catch (error) {
      console.error(error)
      setRows([])
      setTotals({})
    }
    setLoading(false)
  }

  const onSave = async (data: Partial<ILoanSubledgerRow>) => {
    if (editItem?.id) await updateLoanSubledger(editItem.id, data)
    else await createLoanSubledger(data)
    setEditItem(null)
    fetch()
  }

  const onDelete = async (item: ILoanSubledgerRow) => {
    const ok = await confirm(`Delete loan "${item.loanId}"?`)
    if (!ok) return
    setLoading(true)
    await deleteLoanSubledger(item.id)
    fetch()
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          Loan Subledger
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 animate-spin" />
            </span>
          )}
        </h1>
        <button className="btn-icon flex items-center gap-1 text-sm" onClick={() => setEditItem({})}>
          <PlusIcon className="w-4 h-4" /> Add
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        One row per loan. WH Outstanding, Inventory Book, and Realized Gain are computed from Status and the warehouse
        advance rate, and feed every other module.
      </p>

      <div className="relative overflow-x-auto">
        <LayoutLoading show={isLoading} />
        <table className="w-full text-sm text-left text-gray-700 min-w-max">
          <thead className="text-xs uppercase bg-gray-100">
            <tr>
              {[
                'Loan ID',
                'Borrower',
                'Product',
                'Note Rate',
                'Funded Amount',
                'Inv. Acct',
                'Status',
                'Warehouse Line',
                'Adv. Rate',
                'WH Outstanding',
                'Inventory Book',
                'Investor',
                'Sale Price',
                'Realized Gain',
                'Escrow Bal.',
                'Funded Date',
                'Servicing',
                'MSR Value',
                'Action',
              ].map((h) => (
                <th key={h} className="px-3 py-2 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={19} className="px-3 py-8 text-center text-gray-500">
                  No loan subledger records yet.
                </td>
              </tr>
            )}
            {rows.map((item) => (
              <tr key={item.id} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 font-medium whitespace-nowrap">{item.loanId}</td>
                <td className="px-3 py-2 whitespace-nowrap">{item.borrower}</td>
                <td className="px-3 py-2 whitespace-nowrap">{item.product}</td>
                <td className="px-3 py-2 text-right">{formatPercent(item.noteRate)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.fundedAmount)}</td>
                <td className="px-3 py-2">{item.invAccountNo}</td>
                <td className="px-3 py-2 whitespace-nowrap">{item.status}</td>
                <td className="px-3 py-2 whitespace-nowrap">{item.warehouseLine || '-'}</td>
                <td className="px-3 py-2 text-right">{formatPercent(item.advanceRate)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.whOutstanding)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.inventoryBook)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{item.investor || '-'}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.salePrice)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.realizedGain)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.escrowBalance)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{item.fundedDate ? formatDate(item.fundedDate) : '-'}</td>
                <td className="px-3 py-2">{item.servicing || '-'}</td>
                <td className="px-3 py-2 text-right">{formatAmount(item.msrValue)}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-2 items-center">
                    <span
                      className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                      onClick={() => setEditItem(item)}
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                    <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onDelete(item)}>
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="bg-blue-50 font-semibold border-t-2">
                <td colSpan={4} className="px-3 py-2">
                  PORTFOLIO TOTALS
                </td>
                <td className="px-3 py-2 text-right">{formatAmount(totals.fundedAmount)}</td>
                <td colSpan={4} />
                <td className="px-3 py-2 text-right">{formatAmount(totals.whOutstanding)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(totals.inventoryBook)}</td>
                <td colSpan={2} />
                <td className="px-3 py-2 text-right">{formatAmount(totals.realizedGain)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(totals.escrowBalance)}</td>
                <td colSpan={2} />
                <td className="px-3 py-2 text-right">{formatAmount(totals.msrValue)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {editItem && (
        <EditLoanSubledgerDialog
          item={editItem}
          warehouseLineOptions={warehouseLineOptions}
          onClose={() => setEditItem(null)}
          onSave={onSave}
        />
      )}
    </div>
  )
}
